import { Component, inject, OnInit, HostListener, ChangeDetectorRef } from '@angular/core';
import { HasPermissionDirective } from '../../directives/has-permission.directive';
import { RbacPermissions } from '../../services/rbac/rbac-permissions';
import { ColDef, GridReadyEvent, PaginationChangedEvent, CellClickedEvent } from 'ag-grid-community';
import {
  AG_GRID_PAGE_SIZE_OPTIONS,
  AG_GRID_DEFAULT_PAGE_SIZE,
  AG_GRID_DEFAULT_ROW_HEIGHT_PX,
  AG_GRID_EMPTY_BODY_ROW_PX,
  AG_GRID_HEADER_AND_PAGING_PX,
  syncInfiniteRowModelPageSize,
} from '../../shared/ag-grid/ag-grid-pagination.util';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { DeviceService } from '../../services/device-service/device.service';
import { CustomerService } from '../../services/customer-service/customer.service';
import moment from 'moment';
import { get } from 'lodash';
import { AgGridCopyCellTooltipDirective } from '../../shared/ag-grid/ag-grid-copy-cell-tooltip.directive';
import {
  createUtcDateFilterParams,
  formatUtcDateTimeCell,
  mapAgGridFilterToApi,
  withDateColumnLayout,
} from '../../shared/ag-grid/ag-grid-date-column.util';
import {
  CUSTOMER_DROPDOWN_SELECT_LABEL,
  GROUP_DROPDOWN_SELECT_CUSTOMER_FIRST,
  getGroupDropdownEmptyLabel,
  hasConcreteCustomerSelected,
  isGroupDropdownDisabled,
} from '../../services/group-dropdown.util';

@Component({
  selector: 'app-configuration-history',
  standalone: true,
  imports: [
    AgGridAngular,
    MatIconModule,
    MatButtonModule,
    AgGridModule,
    CommonModule,
    FormsModule,
    RouterLink,
    HasPermissionDirective,
    AgGridCopyCellTooltipDirective,
  ],
  templateUrl: './configuration-history.component.html',
  styleUrl: './configuration-history.component.scss',
})
export class ConfigurationHistoryComponent implements OnInit {
  readonly RbacPermissions = RbacPermissions;
  private readonly cdr = inject(ChangeDetectorRef);
  private syncingHeaderColumnFilters = false;

  constructor(
    private deviceService: DeviceService,
    private customerService: CustomerService
  ) {}

  gatewayId: any;
  total_number_of_records = 0;
  public router = inject(Router);

  customerList: any[] = [];
  groups: any[] = [];
  selectedCustomer = 'All';
  selectedGroup = '';
  groupsLoaded = true;
  groupDropdownEmptyLabel = GROUP_DROPDOWN_SELECT_CUSTOMER_FIRST;
  isGroupDropdownDisabled = true;
  readonly customerDropdownSelectLabel = CUSTOMER_DROPDOWN_SELECT_LABEL;

  private refreshGroupDropdownState(): void {
    const hasCustomer = hasConcreteCustomerSelected(this.selectedCustomer);
    const count = this.groups?.length ?? 0;
    this.groupDropdownEmptyLabel = getGroupDropdownEmptyLabel(
      hasCustomer,
      count,
      this.groupsLoaded
    );
    this.isGroupDropdownDisabled = isGroupDropdownDisabled(
      hasCustomer,
      count,
      this.groupsLoaded
    );
  }

  private applyLoadedGroups(groups: any[] | null | undefined): void {
    this.groups = groups || [];
    this.groupsLoaded = true;
    if (this.groups.length === 0) {
      this.selectedGroup = '';
      sessionStorage.removeItem('selectedGroupConHistory');
    }
    this.refreshGroupDropdownState();
  }

  defaultColDef: ColDef = {
    sortable: true,
    sortingOrder: ['asc', 'desc'],
    resizable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
  };

  private readonly textFilterParams = {
    filterOptions: ['contains'],
    defaultOption: 'contains',
    trimInput: true,
    maxNumConditions: 1,
    suppressFilterButton: true,
  };

  private readonly dateColumnFilterParams = createUtcDateFilterParams();

  private readonly customerSearchPayload = {
    page: 0,
    size: 500,
    filters: [{ field: '', operator: '', value: '' }],
    sorts: [{ field: 'customerName', direction: 'asc' }],
  };

  columnDefs: ColDef[] = [
    {
      headerName: 'Device ID',
      field: 'gatewayId',
      flex: 1,
      minWidth: 130,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        return `<span class="ag-grid-link">${
          params?.data?.gatewayId?.toString() ? params?.data?.gatewayId?.toString() : 'N/A'
        }</span>`;
      },
    },
    {
      headerName: 'Customer',
      field: 'customerName',
      flex: 1,
      minWidth: 140,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        return params?.data?.customerName ? params?.data?.customerName : 'N/A';
      },
    },
    {
      headerName: 'Group',
      field: 'groupName',
      flex: 1,
      minWidth: 120,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        return params?.data?.groupName ? params?.data?.groupName : 'N/A';
      },
    },
    {
      headerName: 'Asset ID',
      field: 'assetName',
      flex: 1,
      minWidth: 130,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        return params?.data?.assetName ? params?.data?.assetName : 'N/A';
      },
    },
    {
      headerName: 'Firmware Version',
      field: 'firmwareVersion',
      flex: 1,
      minWidth: 130,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        return params?.data?.firmwareVersion?.toString()
          ? params?.data?.firmwareVersion?.toString()
          : 'N/A';
      },
    },
    {
      headerName: 'Config Version',
      field: 'configVersion',
      flex: 1,
      minWidth: 120,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        return params?.data?.configVersion?.toString()
          ? params?.data?.configVersion?.toString()
          : 'N/A';
      },
    },
    withDateColumnLayout({
      headerName: 'Check In Time (UTC)',
      field: 'dateTime',
      filter: 'agDateColumnFilter',
      floatingFilter: true,
      filterParams: this.dateColumnFilterParams,
      cellRenderer: (params: any) => formatUtcDateTimeCell(params?.data?.dateTime),
    }),
    {
      headerName: 'Result',
      field: 'response',
      flex: 1,
      minWidth: 110,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        return params?.data?.response?.toString() ? params?.data?.response?.toString() : 'N/A';
      },
    },
    {
      headerName: 'Comment',
      field: 'comment',
      flex: 1,
      minWidth: 120,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      valueGetter: (params) => {
        if (params.data?.comment == undefined) {
          return 'N/A';
        } else if (params.data?.comment == null || params.data?.comment == '') {
          return 'N/A';
        } else {
          return params.data?.comment;
        }
      },
    },
  ];

  contextMenu: { visible: boolean; x: number; y: number } = { visible: false, x: 0, y: 0 };
  selectedCellValue = '';
  contextMenuVisible = false;
  contextMenuPosition = { x: 0, y: 0 };

  rowData: any[] = [];
  paginationPageSize = AG_GRID_DEFAULT_PAGE_SIZE;
  readonly pageSizeOptions = AG_GRID_PAGE_SIZE_OPTIONS;
  /** Host height from loaded rows — page scrolls, not the grid. */
  gridHostHeightPx = AG_GRID_HEADER_AND_PAGING_PX + AG_GRID_EMPTY_BODY_ROW_PX;
  /** Actual rows on the current page (drives height; not the page-size selector). */
  loadedRowCount = 0;
  gridApi: any;
  currentPage: any;
  deviceId: any;
  isLoading = false;

  ngOnInit(): void {
    const storedSelectedCustomer = sessionStorage.getItem('selectedCustomerConHistory');
    if (storedSelectedCustomer) {
      this.selectedCustomer = storedSelectedCustomer;
    }
    const storedSelectedGroup = sessionStorage.getItem('selectedGroupConHistory');
    if (storedSelectedGroup) {
      this.selectedGroup = storedSelectedGroup;
    }

    const storedSorting = sessionStorage.getItem('configHistorySortingState');
    if (storedSorting) {
      const configHistorySortingState = JSON.parse(storedSorting);
      this.payloadOfgetDeviceConfigHistory.sorts[0].field = configHistorySortingState.colId;
      this.payloadOfgetDeviceConfigHistory.sorts[0].direction = configHistorySortingState.sort;
    }

    this.getAllCustomer();
  }

  getAllCustomer(): void {
    this.customerService.getAllCustomer(this.customerSearchPayload).subscribe((res) => {
      this.customerList = res?.data?.content ?? [];
      if (this.selectedCustomer && this.selectedCustomer !== 'All') {
        this.loadGroupsForCustomer(this.selectedCustomer);
      }
    });
  }

  private loadGroupsForCustomer(customerName: string): void {
    const selectedCustomerObj = this.customerList?.find(
      (obj: any) => obj.customerName === customerName
    );
    if (!selectedCustomerObj?.id) {
      this.applyLoadedGroups([]);
      return;
    }
    this.groupsLoaded = false;
    this.refreshGroupDropdownState();
    this.deviceService.getOtagroupIdList(selectedCustomerObj.id).subscribe((response: any) => {
      this.applyLoadedGroups(response?.data?.content);
    });
  }

  /** Header Customer dropdown → Customer column filter. */
  onHeaderCustomerChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedCustomer = value || 'All';
    sessionStorage.setItem('selectedCustomerConHistory', this.selectedCustomer);

    this.groups = [];
    this.selectedGroup = '';
    sessionStorage.removeItem('selectedGroupConHistory');

    if (this.selectedCustomer !== 'All') {
      this.groupsLoaded = false;
      this.refreshGroupDropdownState();
      this.loadGroupsForCustomer(this.selectedCustomer);
    } else {
      this.groupsLoaded = true;
      this.refreshGroupDropdownState();
    }

    this.applyHeaderFiltersToGridColumns({
      customerName: this.selectedCustomer === 'All' ? null : this.selectedCustomer,
      groupName: null,
    });
  }

  /** Header Group dropdown → Group column filter. */
  onHeaderGroupChange(event: Event): void {
    this.selectedGroup = (event.target as HTMLSelectElement).value || '';
    if (this.selectedGroup) {
      sessionStorage.setItem('selectedGroupConHistory', this.selectedGroup);
    } else {
      sessionStorage.removeItem('selectedGroupConHistory');
    }

    this.applyHeaderFiltersToGridColumns({
      groupName: this.selectedGroup || null,
    });
  }

  private applyHeaderFiltersToGridColumns(updates: {
    customerName?: string | null;
    groupName?: string | null;
  }): void {
    if (!this.gridApi) {
      return;
    }

    const model: Record<string, any> = { ...(this.gridApi.getFilterModel() || {}) };
    const textContains = (value: string) => ({
      filterType: 'text',
      type: 'contains',
      filter: value,
    });

    if (updates.customerName !== undefined) {
      if (updates.customerName) {
        model['customerName'] = textContains(updates.customerName);
      } else {
        delete model['customerName'];
      }
    }

    if (updates.groupName !== undefined) {
      if (updates.groupName) {
        model['groupName'] = textContains(updates.groupName);
      } else {
        delete model['groupName'];
      }
    }

    this.syncingHeaderColumnFilters = true;
    this.payloadOfgetDeviceConfigHistory.page = 0;
    this.gridApi.setFilterModel(model);
    queueMicrotask(() => {
      this.syncingHeaderColumnFilters = false;
    });
  }

  /** Column floating filters → header Customer/Group dropdowns. */
  onGridFilterChanged(): void {
    if (!this.gridApi || this.syncingHeaderColumnFilters) {
      return;
    }

    const model = this.gridApi.getFilterModel() || {};
    const customerFilter = String(model['customerName']?.filter ?? '').trim();
    const groupFilter = String(model['groupName']?.filter ?? '').trim();

    if (!customerFilter) {
      if (this.selectedCustomer !== 'All') {
        this.selectedCustomer = 'All';
        sessionStorage.setItem('selectedCustomerConHistory', 'All');
        this.groups = [];
        this.selectedGroup = '';
        sessionStorage.removeItem('selectedGroupConHistory');
        this.groupsLoaded = true;
        this.refreshGroupDropdownState();
      }
    } else {
      const exactCustomer = this.customerList?.find(
        (c: any) => c.customerName === customerFilter
      );
      if (exactCustomer && this.selectedCustomer !== exactCustomer.customerName) {
        this.selectedCustomer = exactCustomer.customerName;
        sessionStorage.setItem('selectedCustomerConHistory', this.selectedCustomer);
        this.loadGroupsForCustomer(exactCustomer.customerName);
      }
    }

    if (!groupFilter) {
      if (this.selectedGroup) {
        this.selectedGroup = '';
        sessionStorage.removeItem('selectedGroupConHistory');
      }
    } else {
      const exactGroup = this.groups.find((g: any) => g.groupName === groupFilter);
      if (exactGroup && this.selectedGroup !== exactGroup.groupName) {
        this.selectedGroup = exactGroup.groupName;
        sessionStorage.setItem('selectedGroupConHistory', this.selectedGroup);
      } else if (!exactGroup && this.groups.length === 0 && this.selectedCustomer !== 'All') {
        this.selectedGroup = groupFilter;
        sessionStorage.setItem('selectedGroupConHistory', this.selectedGroup);
      }
    }
  }

  getNestedProperty(obj: any, path: string): any {
    return get(obj, path, 'N/A');
  }

  onCellContextMenu(event: any) {
    event.event.preventDefault();
    const columnField = event.colDef.field;
    let cellValue = 'N/A';
    if (event.colDef.cellRenderer) {
      const renderedValue = event.colDef.cellRenderer({
        data: event.data,
        colDef: event.colDef,
        value: event.value,
      });
      if (typeof renderedValue === 'string') {
        cellValue = renderedValue.replace(/<[^>]*>/g, '');
      } else if (renderedValue?.outerText) {
        cellValue = renderedValue.outerText;
      }
    } else {
      cellValue = this.getNestedProperty(event.data, columnField);
    }

    if (event.node && event.node.data) {
      this.selectedCellValue = cellValue;
      this.contextMenuPosition = { x: event.event.clientX, y: event.event.clientY };
      this.contextMenuVisible = true;
    } else {
      this.contextMenuVisible = false;
    }
  }

  handleCopy() {
    navigator.clipboard
      .writeText(this.selectedCellValue)
      .then(() => {
        this.contextMenuVisible = false;
      })
      .catch((err) => console.error('Failed to copy:', err));
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.contextMenuVisible = false;
  }

  preventDefaultContextMenu(event: MouseEvent): void {
    event.preventDefault();
  }

  onPaginationChanged(event: PaginationChangedEvent): void {
    syncInfiniteRowModelPageSize(
      event,
      this.paginationPageSize,
      (newPageSize) => {
        this.paginationPageSize = newPageSize;
        this.payloadOfgetDeviceConfigHistory.size = newPageSize;
        this.payloadOfgetDeviceConfigHistory.page = 0;
      }
    );
    this.currentPage = event.api.paginationGetCurrentPage();
    this.payloadOfgetDeviceConfigHistory.page = this.currentPage;
  }

  payloadOfgetDeviceConfigHistory = {
    page: 0,
    size: AG_GRID_DEFAULT_PAGE_SIZE,
    filters: [{ field: '', operator: '', value: '' }],
    sorts: [
      { field: 'dateTime', direction: 'desc' },
      { field: 'id', direction: 'desc' },
    ],
  };

  dataSource = {
    getRows: (params: any) => {
      const pageSize = this.paginationPageSize || AG_GRID_DEFAULT_PAGE_SIZE;
      this.payloadOfgetDeviceConfigHistory.size = pageSize;
      this.payloadOfgetDeviceConfigHistory.page = Math.floor(params.startRow / pageSize);

      this.sortForAgGrid(params.sortModel);
      this.filterForAgGrid(params.filterModel);
      this.deviceService.getDeviceConfigHistory(this.payloadOfgetDeviceConfigHistory).subscribe({
        next: (data) => {
          const content = Array.isArray(data?.data?.content) ? data.data.content : [];
          this.rowData = content;
          this.total_number_of_records = data?.data?.totalElements ?? 0;

          // Pass full total for pagination (same as Teal / Configuration List).
          // Short pages may leave empty slots; host height + overflow:hidden removes whitespace.
          const reportedTotal = Number(this.total_number_of_records ?? 0);
          const lastRow =
            reportedTotal > 0 ? reportedTotal : params.startRow + content.length;
          params.successCallback(content, lastRow);
          this.syncGridHostHeight(content.length);
        },
        error: () => {
          this.syncGridHostHeight(0);
          params.failCallback();
        },
      });
    },
  };

  /**
   * Size host to the loaded page of rows (not page-size / 70vh).
   * Inline [style.height.px] on ag-grid-angular prevents global min(70vh, …) from winning.
   */
  private syncGridHostHeight(loadedRowCount: number): void {
    const rows = Number.isFinite(loadedRowCount) && loadedRowCount > 0 ? loadedRowCount : 0;
    this.loadedRowCount = rows;
    const bodyPx =
      rows > 0 ? rows * AG_GRID_DEFAULT_ROW_HEIGHT_PX : AG_GRID_EMPTY_BODY_ROW_PX;
    const contentPx = AG_GRID_HEADER_AND_PAGING_PX + bodyPx;
    const minHostPx = AG_GRID_HEADER_AND_PAGING_PX + AG_GRID_EMPTY_BODY_ROW_PX;
    this.gridHostHeightPx = Math.max(minHostPx, contentPx);
    this.cdr.detectChanges();
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    const storedSorting = sessionStorage.getItem('configHistorySortingState');
    if (storedSorting) {
      const configHistorySortingState = JSON.parse(storedSorting);
      this.gridApi.applyColumnState({
        state: [{ colId: configHistorySortingState.colId, sort: configHistorySortingState.sort }],
        applyOrder: false,
      });
    }
    this.restoreStoredFiltersToGrid();
  }

  private restoreStoredFiltersToGrid(): void {
    if (!this.gridApi) {
      return;
    }

    const model: Record<string, any> = {};
    const textContains = (value: string) => ({
      filterType: 'text',
      type: 'contains',
      filter: value,
    });

    if (this.selectedCustomer && this.selectedCustomer !== 'All') {
      model['customerName'] = textContains(this.selectedCustomer);
    }
    if (this.selectedGroup) {
      model['groupName'] = textContains(this.selectedGroup);
    }

    if (Object.keys(model).length === 0) {
      return;
    }

    this.syncingHeaderColumnFilters = true;
    this.gridApi.setFilterModel(model);
    queueMicrotask(() => {
      this.syncingHeaderColumnFilters = false;
    });
  }

  onCellClicked(event: CellClickedEvent) {
    if (event.colDef.field === 'gatewayId') {
      this.deviceId = event?.data?.deviceId;
      this.gatewayId = event?.data?.gatewayId;
      localStorage.setItem('deviceId', this.deviceId);
      localStorage.setItem('gatewayIdDetails', this.gatewayId);
      this.router.navigate(['./device-details']);
    }
  }

  sortForAgGrid(sortModel: any) {
    if (!sortModel?.[0]?.colId) {
      return;
    }
    sessionStorage.setItem('configHistorySortingState', JSON.stringify(sortModel[0]));
    this.payloadOfgetDeviceConfigHistory.sorts[0].field = sortModel[0].colId;
    this.payloadOfgetDeviceConfigHistory.sorts[0].direction = sortModel[0].sort;
  }

  filterForAgGrid(filterModel: any) {
    const newFilters: any[] = [];

    if (filterModel && Object.keys(filterModel).length > 0) {
      Object.keys(filterModel).forEach((key) => {
        const filter = filterModel[key];
        if (!filter) {
          return;
        }
        const mapped = mapAgGridFilterToApi(key, filter);
        if (mapped) {
          newFilters.push(mapped);
        }
      });
    }

    this.payloadOfgetDeviceConfigHistory.filters =
      newFilters.length > 0 ? newFilters : [{ field: '', operator: '', value: '' }];
  }

  downloadConfigHistory() {
    this.isLoading = true;
    const body = {
      page: 0,
      size: this.total_number_of_records,
      filters: this.payloadOfgetDeviceConfigHistory.filters,
      sorts: this.payloadOfgetDeviceConfigHistory.sorts,
      exportMode: true,
    };

    this.deviceService.getDeviceConfigHistory(body).subscribe({
      next: (data) => {
        const csvData = this.convertToCSV(data.data.content);
        this.downloadCSV(csvData, 'ConfigurationHistory.csv');
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error while downloading data:', error);
      },
    });
  }

  convertToCSV(data: any[]): string {
    if (data.length === 0) return '';

    const headers = this.columnDefs.map((col) => {
      if (col.field === 'dateTime') {
        return 'Check In Time (In UTC)';
      }
      return col.headerName;
    });
    const fields = this.columnDefs.map((col) => col.field);
    const headerRow = headers.join(',');

    const rows = data.map((row) => {
      return fields
        .map((field) => {
          if (!field) return 'N/A';
          const colDef = this.columnDefs.find((col) => col.field === field);
          let rawValue = row[field];
          let value = row[field];

          if (colDef?.cellRenderer) {
            value = colDef.cellRenderer({ data: row, value: row[field], colDef });
          } else {
            value = row[field] || 'N/A';
          }

          if (field === 'dateTime' && rawValue && typeof rawValue === 'string') {
            try {
              const parsedDate = moment(rawValue);
              value = parsedDate.isValid()
                ? `="${parsedDate.format('YYYY-MM-DD HH:mm:ss')}"`
                : `"${rawValue}"`;
            } catch {
              value = `"${rawValue}"`;
            }
          }

          if (typeof value === 'string') {
            value = value.replace(/<[^>]*>/g, '');
            if (value.includes(',') || value.includes('"')) {
              value = `"${value.replace(/"/g, '""')}"`;
            }
          }
          return value;
        })
        .join(',');
    });

    return [headerRow, ...rows].join('\r\n');
  }

  downloadCSV(csvData: string, filename: string) {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}
